import { ColorfulEnvelopeIcon, HeadingMedium, useToast } from '@arnold/common';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { FormControl, FormGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { FormErrorFeedback, FormGroupLabel } from '../../components/Common';
import { cookieConsentManager } from '../../components/CookieConsent';
import { SsoLoginMode, useSendMagicLinkMutation } from '../../generated/hooks';
import { redirectToSsoLogin } from '../Sso/sso';
import { normalizeSsoEmail, resolveSsoLoginMode, useSsoLoginMode } from '../Sso/useSsoLoginMode';
import { ColoredBodySmall, FullWidthButton, RoundLogo } from './Common';

interface IProps {
  confirmEmail: (email: string) => void;
  defaultEmail?: string;
  redirect?: string | null;
}

interface IFormValues {
  email: string;
}

const EnterEmail = ({ confirmEmail, defaultEmail, redirect }: IProps) => {
  const [t, i18n] = useTranslation('loginForm');
  const history = useHistory();
  const { addToast } = useToast();
  const [sendMagicLink] = useSendMagicLinkMutation();
  const [isAuthFlowInProgress, setIsAuthFlowInProgress] = useState(false);
  const [currentEmail, setCurrentEmail] = useState(defaultEmail || '');
  const ssoLoginModeState = useSsoLoginMode(currentEmail);
  const ssoRequired = ssoLoginModeState.value === SsoLoginMode.Required;
  const ssoOptional = ssoLoginModeState.value === SsoLoginMode.Optional;
  const ssoBlocked = ssoLoginModeState.value === SsoLoginMode.Blocked;

  useEffect(() => {
    setCurrentEmail(defaultEmail || '');
  }, [defaultEmail]);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email(t('invalidEmail')).required(t('fillEmail')),
  });

  const initialValues: IFormValues = {
    email: defaultEmail || '',
  };

  const handleSubmit = async (values: IFormValues) => {
    if (isAuthFlowInProgress) return;
    setIsAuthFlowInProgress(true);
    try {
      const email = normalizeSsoEmail(values.email);
      const ssoLoginMode = await resolveSsoLoginMode(ssoLoginModeState);
      if (ssoLoginMode === SsoLoginMode.Required) {
        redirectToSsoLogin(email, redirect);
        return;
      }
      confirmEmail(values.email);
    } finally {
      setIsAuthFlowInProgress(false);
    }
  };

  const handleLoginWithSso = async (email: string) => {
    if (isAuthFlowInProgress) return;
    setIsAuthFlowInProgress(true);
    redirectToSsoLogin(normalizeSsoEmail(email), redirect);
  };

  const handleLoginWithMagicLink = async (email: string) => {
    if (isAuthFlowInProgress) return;
    setIsAuthFlowInProgress(true);
    try {
      const normalizedEmail = normalizeSsoEmail(email);
      const ssoLoginMode = await resolveSsoLoginMode(ssoLoginModeState);
      if (ssoLoginMode === SsoLoginMode.Required) {
        redirectToSsoLogin(normalizedEmail, redirect);
        return;
      }
      try {
        await sendMagicLink({ variables: { username: normalizedEmail, redirect } });
      } catch (e) {
        addToast(t('sendingLinkFailed'));
      }
      history.push(
        `/loginWithMagicLink?email=${encodeURIComponent(normalizedEmail)}${
          redirect ? `&redirect=${encodeURIComponent(redirect)}` : ''
        }`,
      );
    } finally {
      setIsAuthFlowInProgress(false);
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={LoginSchema}>
      {({ errors, handleChange, handleSubmit, isSubmitting, values, validateForm }) => (
        <Form onSubmit={handleSubmit} data-cy="enter-email-form">
          <RoundLogo>
            <ColorfulEnvelopeIcon />
          </RoundLogo>
          <HeadingMedium className="text-center">{t('enterEmail')}</HeadingMedium>
          <FormGroup>
            <FormGroupLabel data-icom="login-email-label">{t('email')}</FormGroupLabel>
            <FormControl
              type="email"
              id="email"
              name="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              value={values.email || ''}
              onChange={(e) => {
                handleChange(e);
                setCurrentEmail(e.target.value);
              }}
              isInvalid={!!errors.email}
            />
            {errors.email && <FormErrorFeedback error={errors.email} />}
          </FormGroup>
          {ssoBlocked && (
            <div className="alert alert-danger mt-3" role="alert">
              <strong>Login unavailable.</strong> Your email domain is not authorized for SSO in one of your
              organizations. Please contact your administrator.
            </div>
          )}
          {ssoOptional && !ssoBlocked && (
            <>
              <div className="mb-3">
                <FullWidthButton
                  variant="primary"
                  type="button"
                  disabled={isSubmitting || isAuthFlowInProgress || !!errors.email}
                  onClick={async () => {
                    const validationErrors = await validateForm();
                    if (!validationErrors.email) {
                      handleLoginWithSso(values.email);
                    }
                  }}
                  data-icom="sso-login"
                  data-cy="sso-login"
                >
                  {t('loginWithSso')}
                </FullWidthButton>
              </div>
              <ColoredBodySmall className="mt-5 mb-3">{t('or')}</ColoredBodySmall>
            </>
          )}
          {!ssoBlocked && (
            <div>
              <FullWidthButton
                variant={ssoOptional ? 'outline-primary' : 'primary'}
                type="submit"
                disabled={isSubmitting || isAuthFlowInProgress || !!errors.email}
                data-icom="login-submit"
                data-cy="login-submit"
              >
                {ssoRequired ? t('loginWithSso') : ssoOptional ? t('loginWithPassword') : t('signInWithEmail')}
              </FullWidthButton>
            </div>
          )}
          {!ssoRequired && !ssoBlocked && (
            <>
              {!ssoOptional && <ColoredBodySmall className="mt-5 mb-3">{t('or')}</ColoredBodySmall>}
              <div>
                <FullWidthButton
                  variant="outline-primary"
                  type="button"
                  disabled={isSubmitting || isAuthFlowInProgress || !!errors.email}
                  onClick={async () => {
                    const validationErrors = await validateForm();
                    if (!validationErrors.email) {
                      handleLoginWithMagicLink(values.email);
                    }
                  }}
                  data-icom="magic-link-login"
                  data-cy="magic-link-login"
                >
                  {t('magicLinkButton')}
                </FullWidthButton>
              </div>
              <ColoredBodySmall className="mt-5 mb-3">{t('magicLinkInfo')}</ColoredBodySmall>
            </>
          )}
          <ColoredBodySmall className="mt-9">
            {t('dontHaveAccount')} <Link to={`/register/${i18n.language}`}>{t('signUp')}</Link>
          </ColoredBodySmall>
          <ColoredBodySmall className="mt-4">
            <a
              href=""
              onClick={(e) => {
                // @ts-ignore
                cookieConsentManager?.showPreferences();
                e.preventDefault();
                return;
              }}
            >
              {t('Navbar:cookieSettings')}
            </a>
          </ColoredBodySmall>
        </Form>
      )}
    </Formik>
  );
};

export default EnterEmail;
